"use client";
import React, { FC, forwardRef } from "react";
import Image from "next/image";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GraduationCapIcon } from "@/components/icons";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { PlanType } from "@/services/plan.service";

export const brandCardVariants = cva(
  "min-h-[200px] md:min-h-[216px] sm:px-3 sm:py-5 rounded-xl text-center p-4",
  {
    variants: {
      variant: {
        "Premium Plan": "bg-light-blue-back border border-light-blue",
        "Paid Plan": "bg-golden-yellow border border-grey-300",
        "Free Plan": "bg-white border border-grey-200",
      },
    },
    defaultVariants: {
      variant: "Free Plan",
    },
  },
);

export interface BrandCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof brandCardVariants> {
  brand_id: number;
  name: string;
  subscribers: number;
  description: string;
  brand_logo: string | null;
  brand_plan?: PlanType;
}

const BrandCard: FC<BrandCardProps> = ({
  variant,
  brand_id,
  brand_plan,
  className,
  name,
  subscribers,
  description,
  brand_logo,
}) => {
  const t = useTranslations();
  const locale = useLocale();

  return (
    <Link href={`/brand/${brand_id}`} className="w-full">
      <div className="">
        <div className="relative">
          {brand_logo ? (
            <img
              src={brand_logo}
              alt={name}
              className="h-[151px] md:h-[191px] border-[#E3E3E3s] bg-white border-[1px] rounded-[8px] w-full object-cover"
            />
          ) : (
            <GraduationCapIcon size={50} />
          )}
          {brand_plan && brand_plan?.slug_kr && brand_plan.slug_en ? (
            <div className="text-[11px] absolute bg-black/60 px-2 py-0.5 rounded-full text-white top-2 right-2 font-[700]">
              {locale === "en" ? brand_plan?.slug_en : brand_plan?.slug_kr}
            </div>
          ) : null}
        </div>

        <h4 className="text-[16px] line-clamp-1 font-[700] my-1 text-golden-pri md:text-[18px]">
          {name}
        </h4>
        <p className="text-[12px]  text-[#AAACB0] mt-2 font-[700]">
          {subscribers} {t("brand.subscriber")}
        </p>
        <p className="text-[12px] md:text-[14px] leading-2 font-[400] text-grey-400 line-clamp-2 break-words">
          {description}
        </p>
      </div>
    </Link>
  );
};

export default BrandCard;
